import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import { PRIMARY_KUBBE, DARK_KUBBE } from "../../../constants/Colors";
import { FaEnvelope, FaInstagram, FaWhatsapp, FaPhone } from "react-icons/fa";
import { postContactUs } from "../../../api/modules";
import { errorToast, successToast } from "../../../services/toastService";
import { forwardRef, useState } from "react";
import {
  contactMail,
  contactPhone,
  instagram,
  whatsApp,
} from "../../Dashboard/components/AppLink/AppLink";
import { IoCheckbox } from "react-icons/io5";

const ContactSection = styled.section`
  background-color: ${PRIMARY_KUBBE};
  padding: 40px 20px;
  text-align: center;
  h2 {
    padding-bottom: 30px;
    font-weight: bold;
    color: white;
  }
`;

const FormWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const StyledField = styled(Field)`
  width: 100%;
  padding: 12px 15px;
  border-radius: 15px;
  border: none;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const StyledTextarea = styled(Field)`
  width: 100%;
  padding: 12px 15px;
  border-radius: 15px;
  border: none;
  font-size: 1rem;
  resize: none;
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const SubmitWrapper = styled.div`
  grid-column: 1 / -1;
  text-align: right;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const SubmitButton = styled.button`
  background-color: white;
  color: #4caf50;
  padding: 10px 25px;
  margin-top: 20px;
  border-radius: 25px;
  border: none;
  cursor: pointer;
  font-weight: bold;
  transition: 0.3s;

  &:hover {
    background-color: #f1f1f1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: red;
  font-size: 0.9rem;
  text-align: left;
`;

const ContactInfo = styled.ul`
  margin-top: 30px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  padding: 0;
  list-style: none;
  color: ${DARK_KUBBE};
  font-size: 0.95rem;

  li {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  li + li {
    position: relative;
    padding-left: 16px;
  }
  li + li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 18px;
    background: rgba(0, 0, 0, 0.35);
  }

  a {
    color: ${DARK_KUBBE};
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  a:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    gap: 12px 20px;
    li {
      width: 100%;
      justify-content: center;
    }
    li + li {
      padding-left: 0;
    }
    li + li::before {
      display: none;
    }
  }
`;

const SuccessMessageWrapper = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: ${({ visible }) =>
    visible ? "translateY(0)" : "translateY(20px)"};
  transition: opacity 0.4s ease, transform 0.4s ease;
  background-color: ${PRIMARY_KUBBE};
  color: white;
  text-align: center;
  padding: 20px;
`;

const ContactForm = forwardRef((props, ref) => {
  const [sent, setSent] = useState(false);

  return (
    <ContactSection ref={ref}>
      {!sent && <h2>CONTACTANOS:</h2>}
      <FormWrapper>
        {!sent ? (
          <Formik
            initialValues={{
              name: "",
              email: "",
              phone: "",
              message: "",
            }}
            validationSchema={Yup.object({
              name: Yup.string().required("El nombre es obligatorio"),
              email: Yup.string()
                .email("Email inválido")
                .required("El email es obligatorio"),
              phone: Yup.string()
                .matches(/^\+?\d{12}$/, "Ej. +543811234456")
                .required("Teléfono obligatorio"),
              message: Yup.string().required("La consulta es obligatoria"),
            })}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              setSubmitting(true);
              const sanitizedValues = {
                ...values,
                phone: values?.phone?.replace(/^\+/, ""),
              };
              const { contact, msg } = await postContactUs(sanitizedValues);
              if (contact) {
                /* successToast(contact); */
                setSent(true);
              } else {
                errorToast(msg || "Intente nuevamente");
              }
              resetForm();
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, setFieldValue, setFieldTouched, values }) => (
              <Form>
                <FormGrid>
                  <LeftColumn>
                    <div>
                      <StyledField
                        name="name"
                        placeholder="Nombre"
                        value={values.name}
                        onChange={(e) => {
                          setFieldValue("name", e.target.value);
                        }}
                        onBlur={() => {
                          setFieldTouched("name", true);
                        }}
                      />
                      <ErrorMessage name="name" component={ErrorText} />
                    </div>

                    <div>
                      <StyledField
                        name="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={(e) => {
                          setFieldValue("email", e.target.value);
                        }}
                        onBlur={() => {
                          setFieldTouched("email", true);
                        }}
                      />
                      <ErrorMessage name="email" component={ErrorText} />
                    </div>

                    <div>
                      <StyledField
                        name="phone"
                        placeholder="Tel/Cel"
                        value={values.phone}
                        onChange={(e) => {
                          setFieldValue("phone", e.target.value);
                        }}
                        onBlur={() => {
                          setFieldTouched("phone", true);
                        }}
                      />
                      <ErrorMessage name="phone" component={ErrorText} />
                    </div>
                  </LeftColumn>

                  <div>
                    <StyledTextarea
                      name="message"
                      as="textarea"
                      placeholder="Consulta"
                      value={values.message}
                      onChange={(e) => {
                        setFieldValue("message", e.target.value);
                      }}
                      onBlur={() => {
                        setFieldTouched("message", true);
                      }}
                    />
                    <ErrorMessage name="message" component={ErrorText} />
                  </div>

                  <SubmitWrapper>
                    <SubmitButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Enviando..." : "Enviar"}
                    </SubmitButton>
                  </SubmitWrapper>
                </FormGrid>
              </Form>
            )}
          </Formik>
        ) : (
          <SuccessMessageWrapper visible={sent}>
            <h3>
              <IoCheckbox className="mr-2" />
              ¡Consulta enviada correctamente!
            </h3>
            <p className="mt-3 mb-0">
              Recibimos tu mensaje y en breve nos vamos a poner en contacto.
            </p>
            <p>Gracias por confiar en Buena Ruta. Estamos para ayudarte.</p>
            <SubmitButton onClick={() => setSent(false)}>
              Enviar otro mensaje
            </SubmitButton>
          </SuccessMessageWrapper>
        )}

        <ContactInfo>
          <li>
            <a href={`mailto:${contactMail}`}>
              <FaEnvelope /> {contactMail}
            </a>
          </li>
          <li>
            <a
              href={`https://instagram.com/${instagram}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram /> {`@${instagram}`}
            </a>
          </li>
          <li>
            <a
              href={`https://wa.me/${whatsApp}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsapp /> {`+${whatsApp}`}
            </a>
          </li>
          <li>
            <a href={`tel:+${contactPhone}`}>
              <FaPhone /> {`+${contactPhone}`}
            </a>
          </li>
        </ContactInfo>
      </FormWrapper>
    </ContactSection>
  );
});

export default ContactForm;
